import { Box, ButtonBase, Grid, styled } from '@mui/material';
import { NestedFieldsSelector } from '../../common/fieldsSelectors/NestedFieldsSelector';
import { SelectorValueTypes } from '../../../types/enums/SelectorValueTypes';
import { colors, Typography } from '@sweep-io/sweep-design';
import { Edit } from '@sweep-io/sweep-design/dist/icons';
import FormulaEditor from '../FormulaEditor';
import { useFormulaEditorState } from '../FormulaEditorStateContext';
import { parseSweepFieldPolymorphicID } from '../../common/fieldsSelectors/nestedFieldsSelectorHelper';
import { SF_TEXT_FIELD_TYPES } from '../../../types/ReferencedValuesTypes';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';
import { AutomationActionRowProps } from './types';
import { newEmptyField } from './utils/getNewEmptyField';
import { VerifyURLSelect } from './selectors/VerifyUrlSelect';
import { RelativeSelect } from './selectors/RelativeSelect';
import { LiteralSelect } from './selectors/LiteralSelect';
import { selectFirstValueTypeOption } from './utils/selectFirstValueTypeOption';
import { TypeSelect } from './selectors/TypeSelect';
import { MandatoryStar } from './MandatoryStar';
import { AutomationActionRowButtons } from './AutomationActionRowButtons';
import { useValueFromAnotherObjectEditorState } from '../ValueFromAnotherObjectEditorStateContext';
import ValueFromAnotherObjectEditor from '../ValueFromAnotherObjectEditor';
import { useMemo } from 'react';
import { SfdcDisplayType } from '../../../types/fields';

const NestedSelectorContainer = styled(Box)({
  border: `1px solid ${colors.grey[300]}`,
  borderRadius: '4px',
  backgroundColor: colors.white,
  '& .MuiButtonBase-root.MuiButton-root': {
    justifyContent: 'normal',
    '& span.MuiButton-endIcon': {
      marginLeft: 'auto',
    },
  },
});

export const AutomationActionRow = ({
  initialObjectName,
  objectName,
  automationActionField,
  onChange,
  onDelete,
  onAdd,
  crmOrgId,
  readonly,
  hideRecordTypeIdField,
  disableLookupItems,
  showCreatableFields,
  excludeValueType,
  showVerifiedUrlOption,
  isShowWebhookOption,
}: AutomationActionRowProps) => {
  const { isValueFromAnotherObjectEditorOpenedPerKey, setValueFromAnotherObjectEditorStatePerKey } =
    useValueFromAnotherObjectEditorState();

  const { setFormulaEditorStatePerKey, isFormulaEditorOpenedPerKey } = useFormulaEditorState();
  const { getEnrichedNestedPath, getSalesforceDisplayTypeFromFieldId, getFieldInfoFromId } =
    useSweepFieldsInfo();

  const { _automationActionField, automationFieldType } = useMemo(() => {
    const _automationActionField = automationActionField || newEmptyField();
    const automationFieldType = getSalesforceDisplayTypeFromFieldId(
      _automationActionField._fieldIds,
    );

    return {
      _automationActionField,
      automationFieldType,
    };
  }, [automationActionField, getSalesforceDisplayTypeFromFieldId]);

  const parentField = getFieldInfoFromId(_automationActionField._fieldIds);

  const isFormulaEditorOpened =
    isFormulaEditorOpenedPerKey && isFormulaEditorOpenedPerKey(_automationActionField._id);

  const isValueFromAnotherObjectEditorOpen =
    isValueFromAnotherObjectEditorOpenedPerKey &&
    isValueFromAnotherObjectEditorOpenedPerKey(_automationActionField._id);

  //when the formula editor or valueFromAnotherObject is opened, don't show row action buttons
  const showActionButtons =
    _automationActionField.valueType === SelectorValueTypes.FORMULA
      ? !isFormulaEditorOpened
      : _automationActionField.valueType === SelectorValueTypes.FROM_ANOTHER_OBJECT
        ? !isValueFromAnotherObjectEditorOpen
        : true;

  const filterRecordTypeIdFields = (field: SweepField) =>
    hideRecordTypeIdField ? field.sweepFieldName !== 'Record Type ID' : true;

  const filterNonUpdatableFields = (field: SweepField) => {
    // if field is resolvable return it
    const { isResolvable } = parseSweepFieldPolymorphicID(field.id || '');

    // TODO: SWEEP-2091| Remove isException
    const isException =
      field.properties.apiName?.endsWith('.OwnerId') ||
      field.properties.apiName?.endsWith('.RecordTypeId');

    if (showCreatableFields)
      return (
        isException ||
        field.properties?.updateable !== false ||
        field.properties?.createable === true
      );
    else return isResolvable || isException || field.properties?.updateable !== false;
  }; //typo comes from the original record

  const filterBy = (field: SweepField) =>
    filterRecordTypeIdFields(field) && filterNonUpdatableFields(field) && !field.rollup; //Rollup value is automatically calculated so can't be set here

  // show lookup types as selectable values only for text type fields.
  const filterByEndSelectionRestrictLookupIfNotText = (field: SweepField) => {
    const { isResolvable } = parseSweepFieldPolymorphicID(field.id || '');
    const originalSfDisplayType = automationFieldType;
    const givenFieldType = field.sfDisplayType;

    const isReferenceOrId = (sfDisplayType: SfdcDisplayType) =>
      sfDisplayType === SfdcDisplayType.REFERENCE || sfDisplayType === SfdcDisplayType.ID;

    if (
      originalSfDisplayType &&
      !isResolvable &&
      !SF_TEXT_FIELD_TYPES.includes(originalSfDisplayType) &&
      !isReferenceOrId(originalSfDisplayType) &&
      isReferenceOrId(givenFieldType)
    )
      return false;
    return true;
  };

  const renderSelector = () => {
    switch (_automationActionField.valueType) {
      case SelectorValueTypes.RELATIVE:
        return (
          <RelativeSelect _field={_automationActionField} onChange={onChange} readonly={readonly} />
        );

      case SelectorValueTypes.VERIFY_URL:
        return (
          <VerifyURLSelect
            _field={_automationActionField}
            onChange={onChange}
            readonly={readonly}
            showVerifiedUrlOption={showVerifiedUrlOption}
          />
        );

      case SelectorValueTypes.REFERENCE: {
        const parentSfDisplayType = parentField?.sfDisplayType;
        const displayTypesFilter = parentSfDisplayType ? [parentSfDisplayType] : [];
        if (parentSfDisplayType === SfdcDisplayType.REFERENCE) {
          displayTypesFilter.push(SfdcDisplayType.ID);
        }

        return (
          parentField &&
          objectName && (
            <NestedSelectorContainer>
              <NestedFieldsSelector
                readonly={readonly}
                crmOrgId={crmOrgId}
                objectType={initialObjectName || ''}
                nestedPath={
                  _automationActionField.value ? (_automationActionField.value as any) : []
                }
                startIcon={
                  <Box
                    sx={{
                      fontSize: '14px !important',
                      marginRight: '-6px',
                    }}
                  >
                    [{initialObjectName}].
                  </Box>
                }
                onChange={(_sweepField) => {
                  onChange({
                    ..._automationActionField,
                    value: _sweepField.fullPath as any,
                  });
                }}
                displayTypesFilter={displayTypesFilter}
                displayTypesFilterMode="family"
                deprecated_isReferencedValue
              />
            </NestedSelectorContainer>
          )
        );
      }
      case SelectorValueTypes.PRIOR:
        return (
          <NestedSelectorContainer>
            <NestedFieldsSelector
              readonly={readonly}
              crmOrgId={crmOrgId}
              objectType={initialObjectName || ''}
              nestedPath={_automationActionField.value ? (_automationActionField.value as any) : []}
              startIcon={
                <Box
                  sx={{
                    fontSize: '14px !important',
                    marginRight: '-6px',
                  }}
                >
                  [{initialObjectName}].
                </Box>
              }
              onChange={(_sweepField) => {
                onChange({
                  ..._automationActionField,
                  value: _sweepField.fullPath as any,
                });
              }}
              deprecated_isReferencedValue
              disableLookupItemsResolve={
                _automationActionField.valueType === SelectorValueTypes.PRIOR
              }
              displayTypesFilter={automationFieldType}
              displayTypesFilterMode="family"
              filterBy={filterByEndSelectionRestrictLookupIfNotText}
            />
          </NestedSelectorContainer>
        );

      case SelectorValueTypes.LITERAL:
      case SelectorValueTypes.WEBHOOK_RESULT:
      case undefined:
        return (
          <LiteralSelect
            crmOrgId={crmOrgId}
            _field={_automationActionField}
            onChange={onChange}
            readonly={readonly}
            _valueType={_automationActionField.valueType ?? SelectorValueTypes.LITERAL}
            sfDisplayType={automationFieldType}
          />
        );

      case SelectorValueTypes.FORMULA:
        if (!isFormulaEditorOpened) {
          return (
            <ButtonBase
              sx={{ width: '100%' }}
              onClick={() => {
                setFormulaEditorStatePerKey &&
                  setFormulaEditorStatePerKey({ key: _automationActionField._id, value: true });
              }}
            >
              <Box
                display="flex"
                gap={1.5}
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  width: '100%',
                  border: `1px solid ${colors.grey[300]}`,
                  borderRadius: '4px',
                  padding: '9px 12px',
                  backgroundColor: colors.white,
                }}
              >
                <Typography ellipsis variant="body">
                  {_automationActionField.value}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Edit />
                </Box>
              </Box>
            </ButtonBase>
          );
        }

        return <></>;
      case SelectorValueTypes.FROM_ANOTHER_OBJECT:
        if (!isValueFromAnotherObjectEditorOpen) {
          return (
            <ButtonBase
              sx={{ width: '100%' }}
              onClick={() => {
                setValueFromAnotherObjectEditorStatePerKey &&
                  setValueFromAnotherObjectEditorStatePerKey({
                    key: _automationActionField._id,
                    value: true,
                  });
              }}
            >
              <Box
                display="flex"
                gap={1.5}
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  width: '100%',
                  border: `1px solid ${colors.grey[300]}`,
                  borderRadius: '4px',
                  padding: '9px 12px',
                  backgroundColor: colors.white,
                }}
              >
                <Typography ellipsis variant="body">
                  {`Value from ${(_automationActionField?.value as ValueFromAnotherObject)?.objectName?.label ?? 'another object'} record`}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Edit />
                </Box>
              </Box>
            </ButtonBase>
          );
        }

        return <></>;
    }
  };

  const renderFormulaEditor = () => {
    return (
      <FormulaEditor
        crmOrgId={crmOrgId}
        objectName={initialObjectName ?? ''}
        initialValue={_automationActionField.value as string}
        closeEditor={(value?: string) => {
          if (!value && !_automationActionField.value) {
            selectFirstValueTypeOption({ onChange, _field: _automationActionField });
          }
          setFormulaEditorStatePerKey &&
            setFormulaEditorStatePerKey({ key: _automationActionField._id, value: false });
        }}
        updateFormula={({ value, valueFieldIdsArray }) => {
          onChange({
            ..._automationActionField,
            value: value || '',
            valueFieldIdsArray,
          });
        }}
      />
    );
  };

  const renderValueFromAnotherObjectEditor = () => {
    return (
      <ValueFromAnotherObjectEditor
        initialSfDisplayType={automationFieldType}
        crmOrgId={crmOrgId}
        initialObjectName={initialObjectName ?? ''}
        initialValue={_automationActionField.value as ValueFromAnotherObject}
        closeEditor={(value?: ValueFromAnotherObject) => {
          if (
            !value &&
            (!(_automationActionField?.value as ValueFromAnotherObject)?.objectName ||
              !(_automationActionField?.value as ValueFromAnotherObject)?.fieldIds)
          ) {
            selectFirstValueTypeOption({ onChange, _field: _automationActionField });
          }
          setValueFromAnotherObjectEditorStatePerKey &&
            setValueFromAnotherObjectEditorStatePerKey({
              key: _automationActionField._id,
              value: false,
            });
        }}
        onChange={(val: ValueFromAnotherObject) => {
          onChange({
            ..._automationActionField,
            value: val,
          });
        }}
      />
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {_automationActionField._isMandatory && <MandatoryStar />}

      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Grid
            container
            flexWrap="nowrap"
            sx={{
              alignItems: 'center',
              gap: '8px',
              width: 'max-content',
              minWidth: '100%',
              pr: '12px',
            }}
          >
            <Grid item xs={4} zeroMinWidth>
              <Box
                sx={{
                  border: `1px solid ${colors.grey[300]}`,
                  borderRadius: '4px',
                  background: colors.white,
                  width: '100%',
                }}
              >
                <NestedFieldsSelector
                  filterBy={filterBy}
                  disableLookupItemsResolve={disableLookupItems}
                  crmOrgId={crmOrgId}
                  objectType={objectName || ''}
                  nestedPath={getEnrichedNestedPath(_automationActionField._fieldIds)}
                  readonly={_automationActionField._isMandatory || readonly}
                  onChange={(_sweepField) => {
                    onChange({
                      _fieldIds: _sweepField.fieldIds,
                      _id: _automationActionField._id,
                      valueType: SelectorValueTypes.LITERAL,
                      value: '',
                    });
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={3} zeroMinWidth>
              <TypeSelect
                _field={_automationActionField}
                onChange={onChange}
                setFormulaEditorStatePerKey={setFormulaEditorStatePerKey}
                setValueFromAnotherObjectEditorStatePerKey={
                  setValueFromAnotherObjectEditorStatePerKey
                }
                excludeValueType={excludeValueType}
                readonly={readonly}
                showVerifiedUrlOption={showVerifiedUrlOption}
                isShowWebhookOption={isShowWebhookOption}
              />
            </Grid>

            <Grid item xs={5} zeroMinWidth>
              <Box sx={{ width: '100%' }}>{renderSelector()}</Box>
            </Grid>
          </Grid>
        </Box>

        {!readonly && showActionButtons && (
          <Box
            sx={{
              position: 'sticky',
              right: 0,
              backgroundColor: 'white',
              paddingLeft: '12px',
              zIndex: 1,
              display: 'flex',
            }}
          >
            <AutomationActionRowButtons
              isFieldUndefined={automationActionField === undefined}
              _field={_automationActionField}
              onAdd={onAdd}
              onDelete={onDelete}
            />
          </Box>
        )}
      </Box>

      {_automationActionField.valueType === SelectorValueTypes.FORMULA && isFormulaEditorOpened && (
        <Box mt={1}>{renderFormulaEditor()}</Box>
      )}
      {_automationActionField.valueType === SelectorValueTypes.FROM_ANOTHER_OBJECT &&
        isValueFromAnotherObjectEditorOpen && (
          <Box mt={1}>{renderValueFromAnotherObjectEditor()}</Box>
        )}
    </Box>
  );
};
